import React, { FC, useCallback, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { IFormField } from 'modules/core/types';
import { useUncontrolledInput } from 'modules/core/hooks';
import { costFormatter, thousandFormatter } from 'modules/core/utils';
import { Spiner } from 'modules/core/components/Spiner/Spiner';
import { EMPTY_STRING, LINE_DELIMITER } from 'modules/core/constants';

import { FormLabel } from './styles';

export interface ILabelField extends IFormField {
  isComponentAnimated?: boolean;
  isSpinerAnimated?: boolean;
  hideValue?: boolean;
}

export const LabelField: FC<ILabelField> = ({
  name,
  validate,
  isNeedFormat = true,
  endAdornment = undefined,
  isComponentAnimated = false,
  isSpinerAnimated = false,
  hideValue = false,
}) => {
  const { controlField, fieldValue } = useUncontrolledInput(name);

  const fieldLabel = useMemo(() => {
    if (hideValue) return EMPTY_STRING;

    if (Array.isArray(fieldValue)) {
      return fieldValue.map((item) => item.label).join(LINE_DELIMITER);
    }

    const inputValue = fieldValue?.label ?? fieldValue?.title ?? fieldValue?.value ?? fieldValue ?? 0;

    if (name.includes('monthlyIncome') && (!inputValue || inputValue === 0)) return 'N/A';

    if (isNeedFormat) return costFormatter(inputValue);

    if (!isNaN(inputValue)) return thousandFormatter(inputValue);

    return inputValue;
  }, [hideValue, fieldValue, isNeedFormat, name]);

  const renderLabel = useCallback(
    () => (
      <FormLabel $isAnimated={isComponentAnimated}>
        <Spiner style={{ display: isSpinerAnimated ? 'block' : 'none', right: 0 }} />
        {fieldLabel}
        {endAdornment}
      </FormLabel>
    ),
    [endAdornment, fieldLabel, isComponentAnimated, isSpinerAnimated],
  );

  return (
    <Controller
      name={name}
      render={renderLabel}
      control={controlField}
      rules={{
        validate,
      }}
    />
  );
};
